import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "contexts/AuthContext";
import { fetchMemberWorkspaces } from "services/workspaceService";

const WorkspaceContext = createContext(null);

const GLOBAL_SELECTED_WORKSPACE_KEY = "selectedWorkspaceId";
const GLOBAL_SELECTED_WORKSPACE_READY_KEY = "selectedWorkspaceReady";
const storageKeyFor = (userId) => `selectedWorkspaceId:${userId || "anonymous"}`;

const WorkspaceProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [workspaceListReady, setWorkspaceListReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find((account) => account?.id === selectedWorkspaceId) ||
      null,
    [workspaces, selectedWorkspaceId],
  );

  const selectWorkspace = useCallback(
    (accountId) => {
      const nextId = accountId || "";
      setSelectedWorkspaceId(nextId);

      if (actorId) {
        const key = storageKeyFor(actorId);
        if (nextId) {
          localStorage.setItem(key, nextId);
          localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextId);
        } else {
          localStorage.removeItem(key);
          localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
        }
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      }
    },
    [actorId],
  );

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated || !actorId) {
      setWorkspaceListReady(false);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      return [];
    }

    try {
      setLoading(true);
      setWorkspaceListReady(false);
      setError(null);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "0");
      const page = await fetchMemberWorkspaces({
        actorId,
        page: 0,
        size: 100,
      });
      const accounts = page.content || [];
      setWorkspaces(accounts);

      const savedId = localStorage.getItem(storageKeyFor(actorId));
      const nextSelectedId =
        accounts.find((account) => account?.id === savedId)?.id ||
        accounts[0]?.id ||
        "";

      setSelectedWorkspaceId(nextSelectedId);
      if (nextSelectedId) {
        localStorage.setItem(storageKeyFor(actorId), nextSelectedId);
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextSelectedId);
      } else {
        localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      }
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);

      return accounts;
    } catch (nextError) {
      setError(nextError);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [actorId, isAuthenticated]);

  useEffect(() => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  const value = useMemo(
    () => ({
      workspaces,
      selectedWorkspace,
      selectedWorkspaceId,
      selectWorkspace,
      refreshWorkspaces,
      workspaceListReady,
      loading,
      error,
    }),
    [
      error,
      loading,
      workspaceListReady,
      workspaces,
      refreshWorkspaces,
      selectWorkspace,
      selectedWorkspace,
      selectedWorkspaceId,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
};

export default WorkspaceProvider;
